import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Brush
} from 'recharts';
import { fetchBoardFullTimeline, selectFullTimeline } from '../../redux/Slices/analyticsSlice';
import Spinner from '../Global/Spinner';
import './scss/FullTimelineModal.scss';
import Close from '../icons/Close';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="tooltip-value" style={{ color: 'var(--primary-text-color)' }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const FullTimelineModal = ({ isOpen, onClose, boardId }) => {
  const dispatch = useDispatch();
  const fullTimeline = useSelector(selectFullTimeline);
  const [dateRange, setDateRange] = useState(90);
  const [viewMode, setViewMode] = useState('completed'); // 'completed', 'created', 'both'

  useEffect(() => {
    if (isOpen && boardId) {
      dispatch(fetchBoardFullTimeline({ boardId, dateRange }));
    }
  }, [dispatch, isOpen, boardId, dateRange]);

  if (!isOpen) return null;

  const timelineData = fullTimeline?.data?.timeAnalytics?.dailyTimeline || [];
  const isLoading = fullTimeline?.loading;
  const error = fullTimeline?.error;

  const processedData = timelineData.map(day => ({
    date: day.date,
    displayDate: day.displayDate,
    completed: day.completed || 0,
    created: day.created || 0,
    total: (day.completed || 0) + (day.created || 0),
    percentage: day.percentage || 0
  }));

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="full-timeline-modal-overlay" onClick={handleClose}>
      <div className="full-timeline-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Full Timeline View</h2>
          <div className="modal-controls">
            <div className="date-range-selector">
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(parseInt(e.target.value))}
                className="date-range-dropdown"
              >
                <option value={90}>Last 90 Days</option>
                <option value={180}>Last 6 Months</option>
                <option value={365}>Last Year</option>
              </select>
            </div>
            <div className="view-mode-selector">
              <button
                className={viewMode === 'completed' ? 'active' : ''}
                onClick={() => setViewMode('completed')}
              >
                Completed
              </button>
              <button
                className={viewMode === 'created' ? 'active' : ''}
                onClick={() => setViewMode('created')}
              >
                Created
              </button>
              <button
                className={viewMode === 'both' ? 'active' : ''}
                onClick={() => setViewMode('both')}
              >
                Both
              </button>
            </div>
            <button className="close-button" onClick={handleClose}>
              <Close size={24} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {isLoading ? (
            <div className="loading-container">
              <Spinner />
              <p>Loading full timeline...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>Error loading timeline: {error}</p>
              <button onClick={() => dispatch(fetchBoardFullTimeline({ boardId, dateRange }))}>
                Retry
              </button>
            </div>
          ) : (
            <div className="timeline-chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <linearGradient id="completedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--brand-color)" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="var(--brand-color)" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="createdGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ffc53d" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#ffc53d" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--outline-color)" opacity={0.2} />
                  <XAxis
                    dataKey="displayDate"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--primary-text-color)', fontSize: 11, opacity: 0.7 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--primary-text-color)', fontSize: 11, opacity: 0.7 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  
                  {(viewMode === 'completed' || viewMode === 'both') && (
                    <Area
                      type="monotone"
                      dataKey="completed"
                      stroke="var(--brand-color)"
                      strokeWidth={2}
                      fill="url(#completedGradient)"
                      dot={{ fill: 'var(--brand-color)', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, fill: 'var(--brand-color)', stroke: 'var(--single-card-background-color)', strokeWidth: 2 }}
                    />
                  )}
                  
                  {(viewMode === 'created' || viewMode === 'both') && (
                    <Area
                      type="monotone"
                      dataKey="created"
                      stroke="#ffc53d"
                      strokeWidth={2}
                      fill="url(#createdGradient)"
                      dot={{ fill: '#ffc53d', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, fill: '#ffc53d', stroke: 'var(--single-card-background-color)', strokeWidth: 2 }}
                    />
                  )}
                  
                  <Brush
                    dataKey="displayDate"
                    height={30}
                    stroke="var(--brand-color)"
                    fill="var(--single-card-background-color)"
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Timeline Statistics */}
              <div className="timeline-stats">
                <div className="stat-card">
                  <h4>Total Cards Created</h4>
                  <div className="stat-value">
                    {processedData.reduce((sum, day) => sum + day.created, 0)}
                  </div>
                </div>
                <div className="stat-card">
                  <h4>Total Cards Completed</h4>
                  <div className="stat-value">
                    {processedData.reduce((sum, day) => sum + day.completed, 0)}
                  </div>
                </div>
                <div className="stat-card">
                  <h4>Average Daily Completion</h4>
                  <div className="stat-value">
                    {(processedData.reduce((sum, day) => sum + day.completed, 0) / processedData.length).toFixed(1)}
                  </div>
                </div>
                <div className="stat-card">
                  <h4>Peak Day</h4>
                  <div className="stat-value">
                    {Math.max(...processedData.map(day => day.completed))} cards
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullTimelineModal;
